import React, { TouchEvent as ReactTouchEvent, useCallback, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { When } from 'react-if'
import LazyHydrate from 'react-lazy-hydration'
import { useMediaQuery } from 'react-responsive'
import { LinkButton } from '@/atoms/Button'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { HeadingXS, LabelLG } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { EmbeddedVideo } from '@/molecules/EmbeddedVideo'
import { Modal } from '@/molecules/Modal'
import { SimpleThumbnail } from '@/molecules/Thumbnail'
import { useBotDetection } from '@/services/BotDetection'
import { useGuildMember } from '@/services/GuildUserService'
import { useUserRegion } from '@/services/UserRegionService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { isGuildFullySupported } from '@/utils/GuildUtil'
import { useSafeTrack } from '@/utils/analytics'
import { getBooleanFromLocalStorage, writeToLocalStorage } from '@/utils/local-storage'
import { TranslateFunction, useTranslate } from '@/utils/translate/translate-client'

export const AnnouncementModal: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const [touchStart, setTouchStart] = useState(0)
  const [touchEnd, setTouchEnd] = useState(0)
  const minSwipeDistance = 250
  const ref = useRef<HTMLDivElement | null>(null)
  const { isGuildMember, loading } = useGuildMember()
  const { t } = useTranslate('common')
  const { region } = useUserRegion()
  const { isBot } = useBotDetection()

  const isGuildSupported = isGuildFullySupported(region)
  const shouldRender = isGuildSupported && !isBot
  const isDesktopOrLaptop = useMediaQuery({ query: '(min-width: 768px)' })

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false)
    writeToLocalStorage('hasSeenWingfeatherModal', true)
  }, [])

  useEffect(() => {
    const currentDate = new Date()
    // 4:30pm on December 10, 2025 in user's local time
    const startDate = new Date('2025-12-10T16:30:00')
    // 11:59:59pm on December 13, 2025 in user's local time
    const cutoffDate = new Date('2025-12-13T23:59:59')

    // Only show between December 10, 2025 4:30pm and December 13, 2025 11:59pm (user's local time)
    if (currentDate < startDate || currentDate > cutoffDate) {
      return
    }

    // Don't show after the 13th of December, 2025
    if (currentDate > cutoffDate) {
      return
    }

    const hasSeenModal = getBooleanFromLocalStorage('hasSeenWingfeatherModal')

    if (!hasSeenModal) {
      if (!loading) {
        setIsModalOpen(true)
      }
    }
  }, [isGuildMember, loading])

  const onTouchStart = useCallback((event: ReactTouchEvent) => {
    setTouchEnd(0)
    setTouchStart(event.targetTouches[0]?.clientY || 0)
  }, [])

  const onTouchMove = useCallback((event: ReactTouchEvent) => setTouchEnd(event.targetTouches[0]?.clientY || 0), [])

  const onTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return
    const html = window.document.getElementsByTagName('html')[0]
    if (!html) return
    html.style.overflow = 'hidden'
    const distance = touchStart - touchEnd
    const isSwipeDown = distance < -minSwipeDistance
    if (isModalOpen && isSwipeDown) {
      handleModalClose()
      html.style.overflow = 'auto'
    }
  }, [handleModalClose, isModalOpen, touchStart, touchEnd])

  // Helper to check if a click is on the Osano consent banner
  const isClickOnOsanoBanner = useCallback((targetNode: Node): boolean => {
    let current: Node | null = targetNode
    while (current && current !== document.body) {
      if (current instanceof HTMLElement) {
        for (const cls of current.classList) {
          if (cls.includes('osano-cm')) return true // Inside cookie consent UI
        }
      }
      current = current.parentNode
    }
    return false
  }, [])

  useEffect(() => {
    // Add event listener to detect clicks on Osano banner for mobile version only
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (!isModalOpen) return
      const targetNode = event.target as Node | null
      if (!targetNode) return
      if (ref.current?.contains(targetNode)) return // Click inside the mobile modal panel
      if (isClickOnOsanoBanner(targetNode)) return // Click on Osano banner

      handleModalClose()
    }

    if (!isDesktopOrLaptop) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('touchstart', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        document.removeEventListener('touchstart', handleClickOutside)
      }
    }
  }, [handleModalClose, isModalOpen, isDesktopOrLaptop, isClickOnOsanoBanner])

  useEffect(() => {
    if (isModalOpen) {
      setIsVisible(true)
    } else {
      setIsVisible(false)
    }
  }, [isModalOpen])

  if (!shouldRender) return null

  return (
    <LazyHydrate whenVisible>
      <>
        <When condition={isDesktopOrLaptop}>
          <Modal
            isOpen={isModalOpen}
            onClose={handleModalClose}
            panelClasses="md:min-w-[88vw] lg:min-w-min !p-4 !pb-8 !max-w-[834px]"
            className=""
          >
            <AnnouncementModalBody
              isGuildMember={isGuildMember}
              t={t}
              handleModalClose={handleModalClose}
              loading={loading}
            />
          </Modal>
        </When>
        <When condition={!isDesktopOrLaptop}>
          {isVisible && (
            <div
              className={classNames(
                'fixed inset-0 z-[1999]  backdrop-opacity-70 transition-all',
                isVisible ? 'bg-core-gray-950/70' : 'bg-transparent',
              )}
            />
          )}
          <div
            ref={ref}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            className={classNames(
              'box-border fixed bottom-0 left-0 z-[2000] max-h-[90vh] w-full rounded-t-3xl border-t bg-core-gray-100 transition-all duration-300 ease-in-out',
              'sm:max-w-[400px] sm:right-0 sm:mx-auto sm:rounded-b-3xl sm:overflow-hidden sm:h-fit',
              isVisible
                ? 'translate-y-0 sm:my-auto sm:bottom-0 sm:top-0 sm:opacity-1'
                : 'translate-y-full sm:opacity-0',
            )}
          >
            <div className="p-4 pb-8">
              <AnnouncementModalBody
                isGuildMember={isGuildMember}
                t={t}
                handleModalClose={handleModalClose}
                loading={loading}
              />
            </div>
          </div>
        </When>
      </>
    </LazyHydrate>
  )
}

interface AnnouncementModalBodyProps {
  isGuildMember: boolean
  loading: boolean
  t: TranslateFunction
  handleModalClose: () => void
}

const AnnouncementModalBody: React.FC<AnnouncementModalBodyProps> = ({
  isGuildMember,
  loading,
  t,
  handleModalClose,
}) => {
  const track = useSafeTrack()
  const [loadPlayer, setLoadPlayer] = useState(false)

  const handleClick = useCallback(() => {
    track('Announcement Modal CTA Clicked', isGuildMember)
    handleModalClose()
  }, [handleModalClose, isGuildMember, track])

  const handleLoadPlayer = useCallback(() => {
    setLoadPlayer((prev) => !prev)
  }, [])

  return (
    <div className="flex flex-col text-black">
      <div className="flex items-center justify-end">
        <div onClick={handleModalClose} className="cursor-pointer">
          <CloseIcon color="black" size={24} className="cursor-pointer" />
        </div>
      </div>
      <div className="flex flex-col gap-y-4 md:gap-y-6 md:px-6">
        <HeadingXS weight="bold" className="md:photon-heading-sm xl:photon-heading-md text-pretty">
          {t('wingfeatherSagaSeason3BingeNow', 'The Wingfeather Saga Season 3 is here! Binge the season now!')}
        </HeadingXS>
        <When condition={!loadPlayer}>
          <SimpleThumbnail
            imageUrl={getCloudinaryImageUrl({
              path: '/v1765307342/angel-studios/projects/wingfeather-saga/General_YT_Thumbnail_-_2025-10-14T103007.632.webp',
              height: 425,
              width: 754,
            })}
            showButton
            onClick={handleLoadPlayer}
            className={'rounded-2xl'}
          />
        </When>
        <When condition={loadPlayer}>
          <div className="overflow-hidden rounded-2xl">
            <EmbeddedVideo
              className="w-full"
              title={t(
                'wingfeatherSagaSeason3OfficialTrailer',
                'The Wingfeather Saga: Season 3 | Official Trailer | Angel',
              )}
              url={`https://www.youtube.com/embed/ejWuiCaI3TY?autoplay=1&si=vLI6gX7Ca_7JvIX7`}
            />
          </div>
        </When>
        <div className="flex flex-col items-center gap-y-2 text-center md:gap-y-6">
          <div className="mx-auto w-fit">
            {!loading && (
              <>
                <When condition={isGuildMember}>
                  <div className="flex w-full flex-col items-center gap-4 md:flex-row md:justify-center md:gap-6">
                    <LinkButton
                      className="w-full rounded-[14px] px-6 py-[17px] md:w-fit"
                      onClick={handleClick}
                      href={`${paths.watch.index}/${slugs.wingfeatherSaga}?season=3`}
                      variant="oxide-primary"
                    >
                      <LabelLG>{t('watchNow', 'Watch Now')}</LabelLG>
                    </LinkButton>
                  </div>
                </When>
                <When condition={!isGuildMember}>
                  <div className="flex w-full justify-center">
                    <LinkButton
                      className="w-full rounded-[14px] px-6 py-[17px] md:w-fit"
                      onClick={handleClick}
                      href={`${paths.guild.join}/${slugs.wingfeatherSaga}`}
                      variant="oxide-primary"
                    >
                      <LabelLG>{t('joinToWatch', 'Join To Watch')}</LabelLG>
                    </LinkButton>
                  </div>
                </When>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
